import type { OrderDetailDTO } from "@/modules/orders/domain/contracts";
import { useUpdateOrderItems } from "@/modules/orders/presentation/api/queries";
import type { ProductDTO, ProductVariantDTO } from "@/modules/products/domain/contracts";
import { productsApi } from "@/modules/products/presentation/api/client";
import { formatPriceLak } from "@/shared/lib/format-price";
import { AppImage } from "@/shared/ui/AppImage";
import {
  Button,
  Input,
  Label,
  Modal,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { MinusIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

type DraftLine = {
  variantId: string;
  quantity: number;
  productName: string;
  variantLabel: string;
  unitPrice: number;
  imageFileKey?: string | null;
};

function variantLabelFromOrderItem(
  item: OrderDetailDTO["items"][number],
): string {
  const opts = item.resolvedVariantOptions ?? [];
  if (opts.length === 0) return item.variantSku ?? item.variantId;
  return opts.map((o) => `${o.templateName}: ${o.valueName}`).join(", ");
}

function variantLabelFromDto(v: ProductVariantDTO): string {
  const details = v.optionValuesDetails ?? [];
  if (details.length === 0) return v.sku;
  return details
    .map((d) => {
      const tn = (d.optionName as { lo?: string })?.lo ?? d.optionName?.en ?? "";
      const vn = (d.valueName as { lo?: string })?.lo ?? d.valueName?.en ?? "";
      return `${tn}: ${vn}`;
    })
    .join(", ");
}

function productDisplayName(p: ProductDTO): string {
  const n = p.name as { lo?: string; th?: string; en?: string } | undefined;
  return n?.lo ?? n?.th ?? n?.en ?? p.slug;
}

function linesFromOrder(order: OrderDetailDTO): DraftLine[] {
  return order.items.map((item) => ({
    variantId: item.variantId,
    quantity: item.quantity,
    productName: item.productName ?? "—",
    variantLabel: variantLabelFromOrderItem(item),
    unitPrice: item.price,
    imageFileKey: item.imageFileKey,
  }));
}

type OrderEditItemsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: OrderDetailDTO;
};

export function OrderEditItemsDialog({
  open,
  onOpenChange,
  order,
}: OrderEditItemsDialogProps) {
  const updateItems = useUpdateOrderItems(order.id);
  const [lines, setLines] = useState<DraftLine[]>(() => linesFromOrder(order));
  const [note, setNote] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pickProductId, setPickProductId] = useState<string | null>(null);
  const [pickVariantId, setPickVariantId] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (open) {
      setLines(linesFromOrder(order));
      setNote("");
      setSearch("");
      setPickProductId(null);
      setPickVariantId("");
    }
  }, [open, order]);

  const productsQuery = useQuery({
    queryKey: ["order-edit-items", "products", debouncedSearch],
    queryFn: () =>
      productsApi.list({
        limit: 12,
        offset: 0,
        search: debouncedSearch || undefined,
        status: "active",
      }),
    enabled: open && debouncedSearch.length >= 1,
  });

  const variantsQuery = useQuery({
    queryKey: ["order-edit-items", "variants", pickProductId],
    queryFn: () => productsApi.listVariants(pickProductId!),
    enabled: open && !!pickProductId,
  });

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + l.unitPrice * l.quantity, 0),
    [lines],
  );

  const updateQty = useCallback((variantId: string, delta: number) => {
    setLines((prev) =>
      prev
        .map((l) =>
          l.variantId === variantId
            ? { ...l, quantity: Math.max(1, l.quantity + delta) }
            : l,
        )
        .filter((l) => l.quantity >= 1),
    );
  }, []);

  const removeLine = useCallback((variantId: string) => {
    setLines((prev) => prev.filter((l) => l.variantId !== variantId));
  }, []);

  const addVariantLine = useCallback(
    (product: ProductDTO, variant: ProductVariantDTO) => {
      const productName = productDisplayName(product);
      const variantLabel = variantLabelFromDto(variant);
      const imageFileKey = product.images?.[0]?.fileKey ?? null;

      setLines((prev) => {
        const idx = prev.findIndex((l) => l.variantId === variant.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = {
            ...next[idx]!,
            quantity: next[idx]!.quantity + 1,
          };
          return next;
        }
        return [
          ...prev,
          {
            variantId: variant.id,
            quantity: 1,
            productName,
            variantLabel,
            unitPrice: variant.price,
            imageFileKey,
          },
        ];
      });

      setPickProductId(null);
      setPickVariantId("");
      setSearch("");
    },
    [],
  );

  const handleAddPickedVariant = () => {
    const product = productsQuery.data?.data.find((p) => p.id === pickProductId);
    const variant = variantsQuery.data?.find((v) => v.id === pickVariantId);
    if (!product || !variant) return;
    addVariantLine(product, variant);
  };

  const handleSave = async () => {
    if (lines.length === 0) return;
    if (!note.trim()) return;
    await updateItems.mutateAsync({
      items: lines.map((l) => ({
        variantId: l.variantId,
        quantity: l.quantity,
      })),
      note: note.trim(),
    });
    onOpenChange(false);
  };

  const pickedProduct = productsQuery.data?.data.find((p) => p.id === pickProductId);
  const variantOptions = variantsQuery.data ?? [];

  return (
    <Modal open={open} onOpenChange={onOpenChange} showCloseButton={false}>
      <div className="max-h-[85vh] space-y-4 overflow-y-auto pr-1">
        <div className="flex items-start justify-between border-b pb-3">
          <div>
            <h3 className="font-semibold text-lg">ແກ້ລາຍການສິນຄ້າ</h3>
            <p className="text-muted-foreground text-sm">{order.code}</p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="ປິດ"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-2">
          <Label className="text-muted-foreground text-xs">ລາຍການປັດຈຸບັນ</Label>
          {lines.length === 0 ? (
            <p className="text-muted-foreground text-sm">ບໍ່ມີລາຍການ</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {lines.map((line) => (
                <li
                  key={line.variantId}
                  className="flex items-center gap-3 p-3"
                >
                  <div className="size-10 shrink-0 overflow-hidden rounded border bg-muted">
                    <AppImage
                      fileKey={line.imageFileKey}
                      alt=""
                      className="size-full object-cover"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-sm">
                      {line.productName}
                    </p>
                    <p className="truncate text-muted-foreground text-xs">
                      {line.variantLabel}
                    </p>
                    <p className="text-xs">
                      LAK {formatPriceLak(line.unitPrice)} × {line.quantity}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="size-8"
                      onClick={() => updateQty(line.variantId, -1)}
                      disabled={line.quantity <= 1}
                    >
                      <MinusIcon className="size-3.5" />
                    </Button>
                    <span className="w-6 text-center text-sm tabular-nums">
                      {line.quantity}
                    </span>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="size-8"
                      onClick={() => updateQty(line.variantId, 1)}
                    >
                      <PlusIcon className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8 text-destructive"
                      onClick={() => removeLine(line.variantId)}
                      disabled={lines.length <= 1}
                    >
                      <Trash2Icon className="size-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-2 rounded-md border border-dashed bg-muted/20 p-3">
          <Label className="text-muted-foreground text-xs">ເພີ່ມສິນຄ້າ</Label>
          <Input
            placeholder="ຄົ້ນຫາຊື່ສິນຄ້າ..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPickProductId(null);
              setPickVariantId("");
            }}
          />
          {debouncedSearch.length >= 1 && productsQuery.isFetching ? (
            <p className="text-muted-foreground text-xs">ກຳລັງຄົ້ນຫາ...</p>
          ) : null}
          {productsQuery.data?.data.length ? (
            <ul className="max-h-32 space-y-1 overflow-y-auto">
              {productsQuery.data.data.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className={`w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted ${
                      pickProductId === p.id ? "bg-muted font-medium" : ""
                    }`}
                    onClick={() => {
                      setPickProductId(p.id);
                      setPickVariantId("");
                    }}
                  >
                    {productDisplayName(p)}
                  </button>
                </li>
              ))}
            </ul>
          ) : debouncedSearch.length >= 1 && !productsQuery.isFetching ? (
            <p className="text-muted-foreground text-xs">ບໍ່ພົບສິນຄ້າ</p>
          ) : null}

          {pickProductId && pickedProduct ? (
            <div className="space-y-2 border-t pt-2">
              <p className="text-sm">
                ເລືອກຕົວແປ:{" "}
                <span className="font-medium">
                  {productDisplayName(pickedProduct)}
                </span>
              </p>
              {variantsQuery.isLoading ? (
                <p className="text-muted-foreground text-xs">ກຳລັງໂຫຼດ...</p>
              ) : variantOptions.length === 0 ? (
                <p className="text-muted-foreground text-xs">ບໍ່ມີ variant</p>
              ) : variantOptions.length === 1 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    addVariantLine(pickedProduct, variantOptions[0]!)
                  }
                >
                  ເພີ່ມ {variantLabelFromDto(variantOptions[0]!)}
                </Button>
              ) : (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[200px] flex-1">
                    <Select
                      value={pickVariantId || "__none__"}
                      onValueChange={(v) =>
                        setPickVariantId(v === "__none__" ? "" : v)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="ເລືອກ variant" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {variantOptions.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            {variantLabelFromDto(v)} (LAK{" "}
                            {formatPriceLak(v.price)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    disabled={!pickVariantId}
                    onClick={handleAddPickedVariant}
                  >
                    ເພີ່ມ
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">ຍອດຍ່ອຍ (ປະມານ)</span>
          <span className="font-medium">LAK {formatPriceLak(subtotal)}</span>
        </div>

        <div className="space-y-2">
          <Label htmlFor="order-items-edit-note" className="text-muted-foreground text-xs">
            ເຫດຜົນການແກ້ໄຂ (ບັງຄັບ)
          </Label>
          <Textarea
            id="order-items-edit-note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="ເຊັ່ນ: ກອກສີ/ໄຊສ໌ຜິດ"
          />
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateItems.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            disabled={
              updateItems.isPending ||
              lines.length === 0 ||
              !note.trim()
            }
            onClick={() => void handleSave()}
          >
            {updateItems.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກລາຍການ"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
